import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Shield, ChevronDown, CheckCircle2, AlertTriangle, ArrowRight, Sparkles, PhoneForwarded } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTheme } from '../../contexts/ThemeContext';
type ForwardTarget = 'vexu' | 'other' | 'disabled';
interface ForwardingScenario {
  id: string;
  name: string;
  icon: React.ElementType;
  status: ForwardTarget;
  otherNumber?: string;
}
interface CallForwardingCardProps {
  syncPhase: 'idle' | 'confirm' | 'scanning' | 'synced';
  scenarios: ForwardingScenario[];
  forwardingExpanded: boolean;
  setForwardingExpanded: (expanded: boolean) => void;
  isActivated: boolean;
  startSync: () => void;
  handleDisable: (scenario: ForwardingScenario) => void;
  handleEnable: (scenario: ForwardingScenario) => void;
  /** Opens the shared selector for mobile or landline forwarding. */
  onChooseForwardingNumber: () => void;
  onNavigate: (screen: string) => void;
}
export function CallForwardingCard({
  syncPhase,
  scenarios,
  forwardingExpanded,
  setForwardingExpanded,
  isActivated,
  startSync,
  handleDisable,
  handleEnable,
  onChooseForwardingNumber,
  onNavigate
}: CallForwardingCardProps) {
  const {
    isLight
  } = useTheme();
  const activeRuleCount = scenarios.filter(scenario => scenario.status === 'vexu').length;
  const hasNoConnectedNumber = isActivated && activeRuleCount === 0;
  const isAllCallsActive = scenarios.length === 1 && scenarios[0].id === 'all' && scenarios[0].status === 'vexu';
  const renderScenarioStatus = (scenario: ForwardingScenario) => {
    if (scenario.status === 'vexu') {
      return <div className="flex items-center gap-1.5" data-id="element-442" data-mp="d8nw9etxqls9">
          <span className="w-2 h-2 rounded-full bg-vexu" data-id="element-443" data-mp="d7wcr2dm4eic" />
          <span className={`text-[10px] font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-444" data-mp="32xq6j0zksmz5">
            Active
          </span>
          <button onClick={() => handleDisable(scenario)} className={`ml-2 text-[10px] font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-red-500' : 'text-zinc-500 hover:text-red-400'}`} data-id="element-445" data-mp="2y0pzamqzpe6g">
            Deactivate
          </button>
        </div>;
    }
    return <div className="flex items-center gap-1.5" data-id="element-446" data-mp="2vdp2bz5aqukv">
        <span className={`w-2 h-2 rounded-full ${isLight ? 'bg-slate-300' : 'bg-zinc-600'}`} data-id="element-447" data-mp="2vce0i4vevwwe" />
        <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-448" data-mp="2gmy8ymo23nfn">
          Not Active
        </span>
        <button onClick={() => handleEnable(scenario)} className={`ml-2 text-[10px] font-semibold transition-colors ${isLight ? 'text-vexu-600 hover:text-vexu-700' : 'text-vexu hover:text-vexu-400'}`} data-id="element-449" data-mp="27pe6jaegjl3g">
          Activate
        </button>
      </div>;
  };
  return <div className="space-y-3" data-tour="call-forwarding" data-id="element-450" data-mp="22pjirfpzbbmm">
      <div className="flex items-center justify-between" data-id="element-451" data-mp="1j3hwet4uvrdn">
        <h2 className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-452" data-mp="1j401mf9hxfxp">
          AI Answering Rules
        </h2>
      </div>

      <div className={`rounded-[24px] overflow-hidden transition-colors duration-500 ${isLight ? 'bg-white/80 border border-slate-200/60 shadow-sm backdrop-blur-xl' : 'bg-[#111111]/80 border border-white/5 backdrop-blur-xl'}`} data-id="element-453" data-mp="1t2fi7oawd6sy">
        <AnimatePresence mode="wait" data-id="element-454" data-mp="21zzlf2sbld67">
          {syncPhase === 'idle' && <motion.div key="idle" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="p-6 flex flex-col items-center text-center" data-id="element-455" data-mp="1x2zg4u3aoeg5">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isLight ? 'bg-vexu/10' : 'bg-vexu/15'}`} data-id="element-456" data-mp="2gri97mrf8c04">
                <Sparkles className={`w-8 h-8 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-457" data-mp="e2gxf75dj43i" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-458" data-mp="nu4i8p7fdmw0">
                Activate Your AI Assistant
              </h3>
              <p className={`text-sm mb-6 max-w-[260px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-459" data-mp="3rbmtl6mepc16">
                Your AI assistant can answer calls, take messages, schedule
                meetings, and notify you instantly. Complete one quick
                activation to get started.
              </p>
              <Button onClick={startSync} fullWidth data-id="element-460" data-mp="935ebrhjbmir">
                Activate AI
              </Button>
            </motion.div>}

          {syncPhase === 'synced' && !isActivated && <motion.div key="activate" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="p-6 flex flex-col items-center text-center" data-id="element-461" data-mp="1kmq6j6kme77w">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-462" data-mp="2rc2qf1qjxl0r">
                <Shield className={`w-8 h-8 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-463" data-mp="2hd3ftdm87fb4" />
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-464" data-mp="2dcjj2b5kedpi">
                Activate Vexu.ai
              </h3>
              <p className={`text-sm mb-2 max-w-[260px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-465" data-mp="bkevtcz0g4l4">
                Start protecting your missed calls with AI. Vexu.ai can answer
                when you're busy, unreachable, or your phone is off.
              </p>
              <div className="flex flex-col w-full mt-4 gap-3" data-id="element-466" data-mp="uh71pr3oz74d">
                <div className={`rounded-xl p-3 ${isLight ? 'bg-slate-50' : 'bg-white/5'}`} data-id="element-467" data-mp="gp8ujltjjlau">
                  <div className="flex items-center justify-between" data-id="element-468" data-mp="lp4zbna2bgrg">
                    <span className={`text-xs font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-469" data-mp="19d4setuujj97">
                      Forwarding conditions
                    </span>
                    <span className={`text-xs font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-470" data-mp="zefa4zz1fwsf">
                      0 of 4 active
                    </span>
                  </div>
                </div>
                <Button onClick={() => onNavigate('planSelection')} fullWidth data-id="element-471" data-mp="3p4snejqav1pd">
                  Activate Now
                </Button>
              </div>
            </motion.div>}

          {syncPhase === 'synced' && isActivated && <motion.div key="synced" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-472" data-mp="3cl56d9k3tv2t">
              {/* Collapsed summary — always visible */}
              <button type="button" onClick={() => setForwardingExpanded(!forwardingExpanded)} aria-expanded={forwardingExpanded} className={`w-full text-left px-5 py-4 flex items-center justify-between focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${isLight ? 'focus-visible:ring-vexu-500' : 'focus-visible:ring-vexu'}`} data-id="element-473" data-mp="2xxk4tk2vo3x0">
                <div className="flex items-center gap-3" data-id="element-474" data-mp="2a7ptj6y7inev">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isLight ? 'bg-vexu/10' : 'bg-vexu/10'}`} data-id="element-475" data-mp="2a76go4neyp7p">
                    <Shield className={`w-4.5 h-4.5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-476" data-mp="1toizpgv4zun6" />
                  </div>
                  <div data-id="element-477" data-mp="23lnl3eh5rm2x">
                    <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-478" data-mp="23lxe7axi29cu">
                      {hasNoConnectedNumber ? 'No number connected' : `${activeRuleCount} Answering ${activeRuleCount === 1 ? 'Rule' : 'Rules'} Active`}
                    </p>
                    <p className={`text-[10px] font-medium mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-479" data-mp="2mc5cnfq98bmp">
                      {hasNoConnectedNumber ? 'Choose a mobile number or landline for Vexu' : 'Your AI is answering calls in the selected situations'}
                    </p>
                  </div>
                </div>
                <motion.div animate={{
              rotate: forwardingExpanded ? 180 : 0
            }} transition={{
              duration: 0.2
            }} data-id="element-480" data-mp="fnmw68wlualz">
                  <ChevronDown className={`w-5 h-5 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-481" data-mp="5rjj4807srk9" />
                </motion.div>
              </button>

              {/* Expanded details */}
              <AnimatePresence data-id="element-482" data-mp="tnnubglhj3xr">
                {forwardingExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-483" data-mp="tlkvc72cu0gw">
                    <div className={`${isLight ? 'border-t border-slate-100' : 'border-t border-white/5'}`} data-id="element-484" data-mp="2k4pogl58acin">
                      {hasNoConnectedNumber ? <div className="p-5" data-id="element-485" data-mp="2t54axntxhxnq">
                          <div className="flex items-start gap-3" data-id="element-486" data-mp="2p6ophodhwxcf">
                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-487" data-mp="32xab3kuc3le0">
                              <PhoneForwarded className="h-5 w-5" data-id="element-488" data-mp="3cwj2q71q8nx7" />
                            </div>
                            <div data-id="element-489" data-mp="37xfwwkdxado6">
                              <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-490" data-mp="37ua8l7d4bfti">
                                Connect the number Vexu should answer
                              </h3>
                              <p className={`mt-1 text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-491" data-mp="3hjh5s1nuyc0v">
                                Start by choosing whether you want to forward a
                                mobile number or a landline. We’ll show the
                                right setup steps for that number.
                              </p>
                            </div>
                          </div>
                          <button type="button" onClick={onChooseForwardingNumber} className={`mt-5 w-full rounded-2xl p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'bg-vexu/5 hover:bg-vexu/10' : 'bg-vexu/10 hover:bg-vexu/15'}`} data-id="element-492" data-mp="33su88ctu2s6d">
                            <span className="flex items-center gap-3" data-id="element-493" data-mp="25x02rl9l7tla">
                              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-494" data-mp="210hy0le9bv8y">
                                <PhoneForwarded className="h-4 w-4" data-id="element-495" data-mp="2opkpekyp12gn" />
                              </span>
                              <span className="min-w-0 flex-1" data-id="element-496" data-mp="2jq7rn56aaaza">
                                <span className={`block text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-497" data-mp="2jomz1l3j5242">
                                  Choose number to forward
                                </span>
                                <span className={`mt-0.5 block text-[11px] leading-snug ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-498" data-mp="13aoeydkzqsf0">
                                  Set up call forwarding for a mobile number or
                                  landline.
                                </span>
                              </span>
                              <ArrowRight className={`h-4 w-4 shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-499" data-mp="3tpsdcts6oe2u" />
                            </span>
                          </button>
                        </div> : <>
                          {scenarios.map((scenario, index) => {
                    const Icon = scenario.icon;
                    return <div key={scenario.id} className={`flex items-center justify-between p-4 ${index !== scenarios.length - 1 ? isLight ? 'border-b border-slate-100' : 'border-b border-white/5' : ''}`} data-id="element-500" data-mp="2dbsi3w4a9clx" data-mpi={index}>
                                <div className="flex items-center gap-3" data-id="element-501" data-mp="7lp5w5lknafu">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${scenario.status === 'vexu' ? isLight ? 'bg-vexu/10' : 'bg-vexu/10' : isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-502" data-mp="lkf27j0kh635">
                                    <Icon className={`w-4 h-4 ${scenario.status === 'vexu' ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-503" data-mp="3l1wlhczj0lis" />
                                  </div>
                                  <div data-id="element-504" data-mp="36aw1qbtbx57c">
                                    <span className={`text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-505" data-mp="369kw06gbpndl">
                                      {scenario.name}
                                    </span>
                                    <div className="mt-0.5" data-id="element-506" data-mp="2wz88obp0g33t">
                                      {renderScenarioStatus(scenario)}
                                    </div>
                                  </div>
                                </div>
                              </div>;
                  })}

                          {!isAllCallsActive && <div className="px-4 pb-4" data-id="element-507" data-mp="3ftx2c27bar3o">
                              <button type="button" onClick={() => handleEnable({
                      id: 'all',
                      name: 'All Incoming Calls',
                      icon: PhoneForwarded,
                      status: 'disabled'
                    })} className={`mt-4 w-full rounded-2xl p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'bg-vexu/5 hover:bg-vexu/10' : 'bg-vexu/10 hover:bg-vexu/15'}`} data-id="element-508" data-mp="30xp8ea63qbb7">
                                <span className="flex items-center gap-3" data-id="element-509" data-mp="3dhcrdpvuxs0e">
                                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-510" data-mp="3dins1gu0ajna">
                                    <PhoneForwarded className="h-4 w-4" data-id="element-511" data-mp="2zid3wlk2zj5t" />
                                  </span>
                                  <span className="min-w-0 flex-1" data-id="element-512" data-mp="12o8ka5yk5vf8">
                                    <span className={`block text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-513" data-mp="xqgimk842uxu">
                                      Divert all calls
                                    </span>
                                    <span className={`mt-0.5 block text-[11px] leading-snug ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-514" data-mp="1ezsv56hgdnyy">
                                      Let Vexu answer every incoming call
                                      instead.
                                    </span>
                                  </span>
                                  <ArrowRight className={`h-4 w-4 shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-515" data-mp="w7zzt5ewvqo2" />
                                </span>
                              </button>
                            </div>}
                        </>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}